import React from "react";
import { Dialog, Button } from "@/components/ui";
import { Eye } from "lucide-react";

interface HandoffPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  handoffTiming: "on_completion" | "inline";
}

export function HandoffPreviewDialog({ open, onOpenChange, handoffTiming }: HandoffPreviewDialogProps) {
  const isCompletion = handoffTiming === "on_completion";

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="full" className="max-w-6xl max-h-[80vh]">
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2 text-lg font-bold font-sans">
          <Eye className="size-5 text-primary" />
          <span>{isCompletion ? "Flow Blueprint: On Completion Handoff" : "Flow Blueprint: Smart Inline Handoff"}</span>
        </Dialog.Title>
        <Dialog.Description className="text-slate-500 text-sm font-sans leading-normal">
          {isCompletion
            ? "Secures high-quality private feedback first. Happy reviewers are prompted to share their feedback publicly on the success page, while constructive sentiment is securely logged internally."
            : "Dynamically displays public review channels directly within the form for happy clients, while routing neutral or negative reviewers to private feedback first to ensure high conversion and strict FTC anti-gating compliance."}
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="overflow-y-auto space-y-6 py-4 px-6">
        {/* Pros & Cons Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pros */}
          <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-4 space-y-2.5">
            <span className="text-xs font-bold text-emerald-700 uppercase tracking-wider block">Pros / Advantages</span>
            <ul className="list-disc pl-4 space-y-1.5 text-sm text-slate-600 leading-relaxed font-sans">
              {isCompletion ? (
                <>
                  <li>
                    <strong>Guarantees Feedback Capture:</strong> Secures the client's internal rating and detailed
                    comments privately before prompting for public sharing.
                  </li>
                  <li>
                    <strong>Richer Compliance Audits:</strong> Yields comprehensive qualitative data for internal
                    quality control, consumer sentiment audits, and branch performance tracking.
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <strong>Maximized Review Volume:</strong> Happy clients see public platform buttons immediately
                    inside the active form, driving the highest possible conversion rates.
                  </li>
                  <li>
                    <strong>Captures Peak Sentiment:</strong> Routes clients to third-party platforms (like Google or
                    Zillow) while satisfaction is at its peak, reducing drop-off.
                  </li>
                </>
              )}
            </ul>
          </div>

          {/* Cons */}
          <div className="bg-slate-50 border border-slate-200/60 rounded-xl p-4 space-y-2.5">
            <span className="text-xs font-bold text-slate-700 uppercase tracking-wider block">
              Cons / Considerations
            </span>
            <ul className="list-disc pl-4 space-y-1.5 text-sm text-slate-600 leading-relaxed font-sans">
              {isCompletion ? (
                <>
                  <li>
                    <strong>Increased Sharing Friction:</strong> Requires reviewers to complete survey questions before
                    reaching third-party links, which can introduce conversion drop-off.
                  </li>
                  <li>
                    <strong>Clipboard Dependency:</strong> Users are prompted to manually paste their clipboard-copied
                    comments onto external platform success screens.
                  </li>
                </>
              ) : (
                <>
                  <li>
                    <strong>Bypassed Internal Data:</strong> Promoters redirecting to public channels may opt to skip
                    the secondary internal qualitative text fields.
                  </li>
                  <li>
                    <strong>Aborted Submissions:</strong> Reviewers who click out to external sites (like Google) may
                    close the tab before completing the final private feedback.
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Section Mockups with Live GIFs stacked vertically */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest font-mono">
            Interactive Flow Previews
          </h3>
          <div className="flex flex-col gap-6">
            {/* Positive Journey Row */}
            <div className="border border-slate-200 rounded-xl p-5 bg-white flex flex-col md:flex-row gap-6 shadow-xs border-t-4 border-t-emerald-500">
              <div className="space-y-2 md:w-1/3 shrink-0">
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider block">
                  Positive Journey (4-5 Stars)
                </span>
                <ul className="list-disc pl-4 space-y-1.5 text-sm text-slate-500 leading-relaxed font-sans">
                  {isCompletion ? (
                    <>
                      <li>Client selects a positive rating (4 or 5 stars) and writes their detailed feedback.</li>
                      <li>Form is submitted, privately capturing ratings and comments first.</li>
                      <li>Success page loads, automatically copying their written response to the clipboard.</li>
                      <li>Public platform links (Google, Zillow) activate for effortless paste-and-post.</li>
                    </>
                  ) : (
                    <>
                      <li>Client clicks a positive rating (4 or 5 stars) inside the active form.</li>
                      <li>Public review cards (Google, Zillow) expand immediately inline.</li>
                      <li>Client can post to public channels in real-time, matching high sentiment.</li>
                      <li>
                        Secondary internal feedback can still be captured at the bottom, minimizing distraction from the
                        primary public call-to-action.
                      </li>
                    </>
                  )}
                </ul>
              </div>
              <div className="border border-slate-100 rounded-lg overflow-hidden bg-slate-100 flex-1 flex items-center justify-center p-2 min-h-[300px]">
                <img
                  src={
                    isCompletion
                      ? "https://cdn.socialcoach.io/starHybridOnCompletion.gif"
                      : "https://cdn.socialcoach.io/starHybridInline.gif"
                  }
                  alt="Positive Flow Journey"
                  className="max-h-[350px] object-contain rounded"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Negative Journey Row */}
            <div className="border border-slate-200 rounded-xl p-5 bg-white flex flex-col md:flex-row gap-6 shadow-xs border-t-4 border-t-rose-400">
              <div className="space-y-2 md:w-1/3 shrink-0">
                <span className="text-xs font-bold text-rose-600 uppercase tracking-wider block">
                  Constructive Journey (1-3 Stars)
                </span>
                <ul className="list-disc pl-4 space-y-1.5 text-sm text-slate-500 leading-relaxed font-sans">
                  {isCompletion ? (
                    <>
                      <li>Client selects a neutral or low rating (1-3 stars) and outlines details.</li>
                      <li>Form is submitted, privately capturing constructive input.</li>
                      <li>Success page loads with a supportive thank-you note.</li>
                      <li>
                        Third-party public links are rendered at the bottom with decreased visual emphasis. This ensures
                        strict FTC anti-gating compliance while prioritizing internal conflict resolution.
                      </li>
                    </>
                  ) : (
                    <>
                      <li>Client selects a neutral or low rating (1-3 stars) inside the form.</li>
                      <li>An escalated, private feedback field expands inline to capture complaints.</li>
                      <li>
                        Public review platform sharing triggers remain accessible below the textarea to maintain strict
                        FTC anti-gating compliance, but with highly reduced prominence.
                      </li>
                    </>
                  )}
                </ul>
              </div>
              <div className="border border-slate-100 rounded-lg overflow-hidden bg-slate-100 flex-1 flex items-center justify-center p-2 min-h-[300px]">
                <img
                  src={
                    isCompletion
                      ? "https://cdn.socialcoach.io/starHybridOncompletionNegative.gif"
                      : "https://cdn.socialcoach.io/starHybridInlineNegative.gif"
                  }
                  alt="Negative Flow Journey"
                  className="max-h-[350px] object-contain rounded"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </Dialog.Content>

      <Dialog.Footer className="flex justify-end p-4 border-t border-slate-100">
        <Dialog.Close
          render={
            <Button variant="outline" size="sm">
              Close Blueprint
            </Button>
          }
        />
      </Dialog.Footer>
    </Dialog>
  );
}
